/**
 * Canonical map of every Staffbase-supported content locale code to its
 * human-readable language name.
 *
 * Sourced verbatim from the platform reference
 * (https://developers.staffbase.com/references/languages-and-locale-codes/).
 * This is the single source of truth for localization across every widget:
 * adding a locale here makes it resolvable everywhere at once. Order is
 * significant - `defaultLocaleForLanguage` derives the default region for a
 * bare two-letter code from the first matching entry, so keep the most common
 * region first for languages that have several (e.g. `pt_BR` before `pt_PT`).
 */
export const STAFFBASE_LOCALES = {
  af_ZA: 'Afrikaans',
  sq_AL: 'Albanian',
  am_ET: 'Amharic',
  ar_AE: 'Arabic (United Arab Emirates)',
  ar_EG: 'Arabic (Egypt)',
  ar_SA: 'Arabic (Saudi Arabia)',
  eu_ES: 'Basque (Spain)',
  bn_BD: 'Bengali',
  my_MM: 'Burmese',
  bs_BA: 'Bosnian',
  bg_BG: 'Bulgarian',
  zh_CN: 'Chinese (simplified)',
  zh_HK: 'Chinese (traditional)',
  hr_HR: 'Croatian',
  cs_CZ: 'Czech',
  da_DK: 'Danish',
  nl_NL: 'Dutch',
  en_US: 'English',
  et_EE: 'Estonian',
  nl_BE: 'Flemish',
  tl_PH: 'Filipino (Tagalog)',
  fi_FI: 'Finnish',
  fr_FR: 'French (France)',
  fr_CA: 'French (Canada)',
  ka_GE: 'Georgian',
  de_DE: 'German',
  el_GR: 'Greek',
  kl_GL: 'Greenlandic',
  gu_IN: 'Gujarati',
  he_IL: 'Hebrew',
  hi_IN: 'Hindi',
  hu_HU: 'Hungarian',
  is_IS: 'Icelandic',
  id_ID: 'Indonesian (Bahasa Indonesia)',
  it_IT: 'Italian',
  ga_IE: 'Irish',
  ja_JP: 'Japanese',
  kn_IN: 'Kannada',
  rw_RW: 'Kinyarwanda',
  km_KH: 'Khmer',
  ko_KR: 'Korean',
  lo_LA: 'Lao',
  lv_LV: 'Latvian',
  lt_LT: 'Lithuanian',
  mk_MK: 'Macedonian',
  mg_MG: 'Malagasy',
  ms_MY: 'Malay',
  ml_IN: 'Malayalam',
  mr_IN: 'Marathi',
  mi_NZ: 'Māori',
  mn_MN: 'Mongolian',
  ne_NP: 'Nepali',
  no_NO: 'Norwegian',
  ps_AF: 'Pashto / Pushtu',
  fa_AF: 'Persian (Dari)',
  fa_IR: 'Persian (Farsi)',
  pa_IN: 'Punjabi',
  pl_PL: 'Polish',
  pt_BR: 'Portuguese (Brazilian)',
  pt_PT: 'Portuguese (European)',
  ro_RO: 'Romanian',
  ru_RU: 'Russian',
  sr_RS: 'Serbian',
  si_LK: 'Sinhala',
  sk_SK: 'Slovak',
  sl_SI: 'Slovenian',
  so_SO: 'Somali',
  es_ES: 'Spanish (Spain)',
  es_MX: 'Spanish (Mexico)',
  sw_TZ: 'Swahili',
  sv_SE: 'Swedish',
  ta_IN: 'Tamil',
  te_IN: 'Telugu',
  th_TH: 'Thai',
  ti_ER: 'Tigrinya',
  tr_TR: 'Turkish',
  uk_UA: 'Ukrainian',
  ur_PK: 'Urdu',
  uz_UZ: 'Uzbek',
  vi_VN: 'Vietnamese',
  cy_GB: 'Welsh',
  zu_ZA: 'Zulu',
} as const
